import { useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useIsPromoteMobileAppBanner } from '@/components/MobileAppPopup/hooks';
import { isIos } from '@/helpers/common';
import { STORAGE, useCommonStore, useLocalStorageStore } from '@/store';

const SHOW_MOBILE_APP_POPUP_AGAIN_AFTER_DAYS = 7 * 24 * 60 * 60 * 1000;

export const useMobileAppBanner = () => {
  const {
    isEmailConfirmationPopup,
    isMobileAppPopupVisible,
    isAuthenticated,
    isMobileDevice,
    mobileAppCMS,
    isAnyAppsDesktop,
  } = useCommonStore(
    useShallow((state) => ({
      isEmailConfirmationPopup: state.isEmailConfirmationPopup,
      isMobileAppPopupVisible: state.isMobileAppPopupVisible,
      isAuthenticated: state.isAuthenticated,
      isMobileDevice: state.isMobileDevice,
      mobileAppCMS: state.mobileAppCMS,
      isAnyAppsDesktop: state.isAnyAppsDesktop,
    }))
  );

  const [isShowMobileAppBanner, setIsShowMobileAppBanner] = useState(false);

  const isPromoteMobileAppBanner = useIsPromoteMobileAppBanner();

  const isAuthWithoutLocalStorage = useMemo(() => {
    return isAuthenticated && !isEmailConfirmationPopup;
  }, [isAuthenticated, isEmailConfirmationPopup]);

  useEffect(() => {
    if (isAuthWithoutLocalStorage) {
      const lastSeenMobileAppPopup = useLocalStorageStore
        .getState()
        .get(STORAGE.MOBILE_APP_POPUP_LAST_SEEN_DATE);

      const lastSeenDateMobileAppBanner =
        new Date(Number(lastSeenMobileAppPopup)).getTime() || null;

      const isMobileAppPopupVisible =
        !lastSeenDateMobileAppBanner ||
        Date.now() - lastSeenDateMobileAppBanner >=
          SHOW_MOBILE_APP_POPUP_AGAIN_AFTER_DAYS;

      useCommonStore.setState({
        isMobileAppPopupVisible,
      });
    }
  }, [isAuthWithoutLocalStorage]);

  const shouldShowMobileAppBanner = useMemo(() => {
    return isPromoteMobileAppBanner && !isEmailConfirmationPopup;
  }, [
    isEmailConfirmationPopup,
    isMobileAppPopupVisible,
    isPromoteMobileAppBanner,
  ]);

  const shouldRenderAppBanner = useMemo(() => {
    if (
      !shouldShowMobileAppBanner ||
      !mobileAppCMS ||
      !isMobileAppPopupVisible
    ) {
      return false;
    }
    if (isMobileDevice) {
      return isIos()
        ? mobileAppCMS.showIos || mobileAppCMS.showPwa
        : mobileAppCMS.showAndroid;
    }

    return isAnyAppsDesktop;
  }, [
    isMobileDevice,
    shouldShowMobileAppBanner,
    mobileAppCMS,
    isAnyAppsDesktop,
    isMobileAppPopupVisible,
  ]);

  useEffect(() => {
    if (shouldRenderAppBanner) {
      const timer = setTimeout(() => {
        setIsShowMobileAppBanner(true);
      }, 3000);
      return () => clearTimeout(timer);
    } else {
      setIsShowMobileAppBanner(false);
    }
  }, [
    isPromoteMobileAppBanner,
    isEmailConfirmationPopup,
    isMobileAppPopupVisible,
    shouldShowMobileAppBanner,
    isMobileDevice,
    shouldRenderAppBanner,
  ]);

  return { isShowMobileAppBanner };
};
